import React, { useCallback, useEffect, useMemo, useState } from 'react';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';

import { ProviderLocationsTableRowV2 } from './ProviderLocationsTableRowV2';
import { StyledTableCell, StyledTableRow } from './ProviderLocationsTableRowV2.styles';
import {
  Container,
  SearchBarContainer,
  SearchBarContainerInner,
  StyledTable,
  StyledTableHead,
  tablePaginationStyles,
} from './ProviderLocationsTableV2.styles';

import downImg from '../../../assets/images/arrow_downward_24px.svg';
import upImg from '../../../assets/images/arrow_upward_24px.svg';
import { ButtonV2 } from '../../../components/common/v2/ButtonV2/ButtonV2';
import { TextFieldV2 } from '../../../components/common/v2/TextInputV2/TextInputV2';
import { isV2PermissionsEnabled } from '../../../helpers/featureFlags/isV2PermissionsEnabled';
import { isProvider } from '../../../helpers/roles/isProvider';
import { useProviderRbac } from '../../../hooks/useProviderRbac';

const rowsPerPageOptions = [10, 25, 50, 100, 200];

export const ProviderLocationsTableV2 = ({
  providerData = [],
  providerName,
  teamType,
  majorProvidersList,
  refreshLocations,
  totalCount,
  rowsPerPage,
  selectedPage,
  onPageChange,
  onRowsPerPageChange,
  isLoading,
  onSort,
  currentSortField,
  currentSortDirection,
  onSearchChange,
  searchValue,
  onSearchClear,
  onSearch,
  isEmptyResponse,
  onRefreshTransactions,
}) => {
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [searchHovered, setSearchHovered] = useState(false);
  const [hoveredColumn, setHoveredColumn] = useState(null);
  const [columnSortState, setColumnSortState] = useState({
    provider_name: 'ASC',
    formatted_address: 'ASC',
    provider_updated_date: 'DESC',
  });
  const [activeColumn, setActiveColumn] = useState(currentSortField || 'provider_updated_date');

  const sortedProviderData = useMemo(() => {
    return providerData;
  }, [providerData]);

  useEffect(() => {
    setActiveColumn(currentSortField || 'provider_updated_date');
    setColumnSortState((prev) => ({
      ...prev,
      [currentSortField || 'provider_updated_date']: currentSortDirection || 'DESC',
    }));
  }, [currentSortField, currentSortDirection]);

  const toggleSort = useCallback(
    (field) => {
      const currentDirection = columnSortState[field];
      const newDirection = currentDirection === 'ASC' ? 'DESC' : 'ASC';

      setColumnSortState((prev) => ({
        ...prev,
        [field]: newDirection,
      }));

      setActiveColumn(field);

      if (onSort) {
        onSort(field, newDirection);
      }
    },
    [columnSortState, onSort],
  );

  const handleSearchChange = useCallback(
    (searchVal) => {
      if (onSearchChange) {
        onSearchChange(searchVal);
      }
    },
    [onSearchChange],
  );

  const handleClearSearch = useCallback(() => {
    if (onSearchClear) {
      onSearchClear();
    } else if (onSearchChange) {
      onSearchChange('');
    }
  }, [onSearchChange, onSearchClear]);

  const { isProviderInventoryManager } = useProviderRbac();

  const isUserProvider = isV2PermissionsEnabled() ? isProviderInventoryManager : isProvider();

  return (
    <Container>
      {isLoading && <LinearProgress color="primary" sx={{ mb: 1 }} />}
      <TableContainer component={Paper}>
        {!isEmptyResponse && (
          <StyledTable aria-label="collapsible table">
            <StyledTableHead>
              <StyledTableRow>
                <StyledTableCell width="3%">S.#</StyledTableCell>
                <StyledTableCell
                  width="25%"
                  className="sortable"
                  style={{ cursor: 'pointer' }}
                  onClick={() => toggleSort('provider_name')}
                  onMouseEnter={() => setHoveredColumn('provider_name')}
                  onMouseLeave={() => setHoveredColumn(null)}
                >
                  Provider Name
                  {(activeColumn === 'provider_name' || hoveredColumn === 'provider_name') && (
                    <SortArrow
                      isSorted={columnSortState.provider_name}
                      isHovered={
                        hoveredColumn === 'provider_name' && activeColumn !== 'provider_name'
                      }
                    />
                  )}
                </StyledTableCell>
                <StyledTableCell
                  width="55%"
                  className="sortable"
                  style={{ cursor: 'pointer' }}
                  onClick={() => toggleSort('formatted_address')}
                  onMouseEnter={() => setHoveredColumn('formatted_address')}
                  onMouseLeave={() => setHoveredColumn(null)}
                >
                  Address
                  {(activeColumn === 'formatted_address' ||
                    hoveredColumn === 'formatted_address') && (
                    <SortArrow
                      isSorted={columnSortState.formatted_address}
                      isHovered={
                        hoveredColumn === 'formatted_address' &&
                        activeColumn !== 'formatted_address'
                      }
                    />
                  )}
                </StyledTableCell>
                <StyledTableCell
                  width="15%"
                  className="sortable"
                  style={{ cursor: 'pointer' }}
                  onClick={() => toggleSort('provider_updated_date')}
                  onMouseEnter={() => setHoveredColumn('provider_updated_date')}
                  onMouseLeave={() => setHoveredColumn(null)}
                >
                  Last Updated
                  {(activeColumn === 'provider_updated_date' ||
                    hoveredColumn === 'provider_updated_date') && (
                    <SortArrow
                      isSorted={columnSortState.provider_updated_date}
                      isHovered={
                        hoveredColumn === 'provider_updated_date' &&
                        activeColumn !== 'provider_updated_date'
                      }
                    />
                  )}
                </StyledTableCell>
                <TableCell width="2%" />
              </StyledTableRow>
            </StyledTableHead>

            <TableBody>
              {sortedProviderData.map((location, idx) => (
                <ProviderLocationsTableRowV2
                  key={`${location.provider_id || location.SERIAL_NO}-${idx}`}
                  sno={location.SERIAL_NO}
                  row={location}
                  handleSelectOption={setSelectedOptions}
                  selectedOptions={selectedOptions}
                  refreshLocations={refreshLocations}
                  providerName={providerName}
                  userType={teamType}
                  majorProvidersList={majorProvidersList}
                  onRefreshTransactions={onRefreshTransactions}
                />
              ))}
            </TableBody>
          </StyledTable>
        )}
      </TableContainer>

      {!isEmptyResponse && (
        <TablePagination
          component="div"
          sx={tablePaginationStyles}
          count={totalCount}
          page={selectedPage}
          rowsPerPage={rowsPerPage}
          onPageChange={onPageChange}
          onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value))}
          rowsPerPageOptions={rowsPerPageOptions}
        />
      )}
    </Container>
  );
};

const SortArrow = ({ isSorted, isHovered }) => (
  <span className="sortedLocationImage__Icon" style={{ opacity: isHovered ? 0.5 : 1 }}>
    {isSorted === 'DESC' ? <img src={downImg} alt="" /> : <img src={upImg} alt="" />}
  </span>
);
